import cx from 'classnames';
import type {ReactNode} from 'react';
import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';

import type {SelectProps} from '@pexip/components';
import {IconTypes, List, Row} from '@pexip/components';
import type {IndexedDevices, MediaDeviceInfoLike} from '@pexip/media-control';

import {TestId} from '../../../test/testIds';
import type {BlockedBrowserPermissionsInfoType, DeviceError} from '../../types';
import {DeviceSelect} from '../DeviceSelect/DeviceSelect.view';
import {MissingDeviceAlert} from '../DeviceSelect/MissingDeviceAlert.view';

import styles from './DevicesList.module.scss';

export const DevicesList: React.FC<
    {
        requestedAudio?: boolean;
        requestedVideo?: boolean;
        disableAudioInputSelector: boolean;
        disableAudioOutputSelector: boolean;
        disableVideoInputSelector: boolean;
        videoInput?: MediaDeviceInfoLike;
        audioInput?: MediaDeviceInfoLike;
        audioOutput?: MediaDeviceInfoLike;
        devices: IndexedDevices;
        videoInputError: DeviceError | undefined;
        audioInputError: DeviceError | undefined;
        onAudioInputChange: (id: string) => void;
        onAudioOutputChange: (id: string) => void;
        onVideoInputChange: (id: string) => void;
        retryAudioInput?: () => void;
        retryVideoInput?: () => void;
        isLoading?: boolean;
        inputAudioTester?: ReactNode;
        outputAudioTester?: ReactNode;
        learnHowToGrantAccessURL?: string;
        permissionInfoType?: BlockedBrowserPermissionsInfoType;
        setShowHelpVideo?: React.Dispatch<React.SetStateAction<boolean>>;
        showTooltip?: boolean;
    } & Pick<SelectProps, 'sizeModifier'>
> = ({
    audioInput,
    audioInputError,
    audioOutput,
    devices,
    isLoading = false,
    requestedAudio = true,
    requestedVideo = true,
    onAudioInputChange,
    onAudioOutputChange,
    onVideoInputChange,
    retryAudioInput,
    retryVideoInput,
    inputAudioTester,
    outputAudioTester,
    videoInput,
    videoInputError,
    learnHowToGrantAccessURL,
    permissionInfoType,
    sizeModifier,
    setShowHelpVideo,
    showTooltip = false,
    disableAudioInputSelector,
    disableVideoInputSelector,
    disableAudioOutputSelector,
}) => {
    const {t} = useTranslation();
    const [showErrorTooltip, setShowErrorTooltip] = useState({
        audio: false,
        video: false,
    });

    const videoTooltipClick = (visible: boolean) => {
        setShowErrorTooltip({
            audio: false,
            video: visible,
        });
    };

    const audioTooltipClick = (visible: boolean) => {
        setShowErrorTooltip({
            audio: visible,
            video: false,
        });
    };

    const shouldShowMissingVideoAlert =
        !disableVideoInputSelector &&
        videoInputError?.title &&
        (videoInputError.retryOnly || devices.size('videoinput') < 2);
    const shouldShowMissingAudioAlert =
        !disableAudioInputSelector &&
        audioInputError?.title &&
        (audioInputError.retryOnly || devices.size('audioinput') < 2);
    const shouldShowAudioOutput =
        Boolean(audioOutput) || devices.size('audiooutput') > 0;

    return (
        <List spacing="none" className={styles.deviceListWrapper}>
            {shouldShowMissingVideoAlert ? (
                <MissingDeviceAlert
                    className="mb-3"
                    title={videoInputError.title}
                    tooltip={videoInputError.description}
                    deniedDevice={videoInputError.deniedDevice}
                    retry={
                        videoInputError.retriable ? retryVideoInput : undefined
                    }
                    learnHowToGrantAccessURL={learnHowToGrantAccessURL}
                    permissionInfoType={permissionInfoType}
                    data-testid={TestId.MissingVideoDeviceAlert}
                    setIsTooltipVisible={videoTooltipClick}
                    isTooltipVisible={showErrorTooltip.video}
                    setShowHelpVideo={setShowHelpVideo}
                    showTooltip={showTooltip}
                />
            ) : (
                <DeviceSelect
                    className="mb-3"
                    kind="videoinput"
                    isDisabled={isLoading || disableVideoInputSelector}
                    label={t(
                        'settings.device-select.select-camera',
                        'Select camera',
                    )}
                    data-testid={TestId.SelectVideoInput}
                    errorText={videoInputError?.title}
                    errorTextTestId={TestId.SelectVideoInputErrorText}
                    hasError={requestedVideo && !!videoInputError?.title}
                    onDeviceChange={onVideoInputChange}
                    iconType={IconTypes.IconVideoOn}
                    selected={videoInput}
                    devices={devices}
                    sizeModifier={sizeModifier}
                />
            )}
            {shouldShowMissingAudioAlert ? (
                <MissingDeviceAlert
                    className="mb-2"
                    title={audioInputError.title}
                    tooltip={audioInputError.description}
                    deniedDevice={audioInputError.deniedDevice}
                    retry={
                        audioInputError.retriable ? retryAudioInput : undefined
                    }
                    learnHowToGrantAccessURL={learnHowToGrantAccessURL}
                    permissionInfoType={permissionInfoType}
                    data-testid={TestId.MissingAudioDeviceAlert}
                    setIsTooltipVisible={audioTooltipClick}
                    isTooltipVisible={showErrorTooltip.audio}
                    setShowHelpVideo={setShowHelpVideo}
                    showTooltip={showTooltip}
                />
            ) : (
                <DeviceSelect
                    className={inputAudioTester ? 'mb-2' : 'mb-3'}
                    kind="audioinput"
                    isDisabled={isLoading || disableAudioInputSelector}
                    label={t(
                        'settings.device-select.select-microphone',
                        'Select microphone',
                    )}
                    data-testid={TestId.SelectAudioInput}
                    errorText={audioInputError?.title}
                    errorTextTestId={TestId.SelectAudioInputErrorText}
                    hasError={requestedAudio && !!audioInputError?.title}
                    onDeviceChange={onAudioInputChange}
                    iconType={IconTypes.IconMicrophoneOn}
                    selected={audioInput}
                    devices={devices}
                    sizeModifier={sizeModifier}
                />
            )}
            {inputAudioTester && (
                <div
                    className={cx(
                        'p-0',
                        'mb-2',
                        'd-flex',
                        'flex-direction-column',
                        'align-items-end',
                    )}
                >
                    {inputAudioTester}
                    {!shouldShowAudioOutput && outputAudioTester}
                </div>
            )}
            {shouldShowAudioOutput && (
                <>
                    <DeviceSelect
                        isDisabled={isLoading || disableAudioOutputSelector}
                        kind="audiooutput"
                        label={t(
                            'settings.device-select.select-speaker',
                            'Select speaker',
                        )}
                        data-testid={TestId.SelectAudioOutput}
                        onDeviceChange={onAudioOutputChange}
                        iconType={IconTypes.IconSpeakerOn}
                        selected={audioOutput}
                        devices={devices}
                        sizeModifier={sizeModifier}
                        className="m-0"
                    />
                    {outputAudioTester && (
                        <Row className={'p-0 mt-2 justify-content-end'}>
                            {outputAudioTester}
                        </Row>
                    )}
                </>
            )}
        </List>
    );
};
